import { assignUnitToTrip, AssignUnitToTripParams } from './assignUnitToTrip';
import { changeUnitStatus, ChangeUnitStatusParams } from './changeUnitStatus';
import { checkForRequiredLateReasons, IResponseProfileTimestamp } from './checkForRequiredLateReasons';

import { TRIPS } from '@/constants';
import { container } from '@/container';
import { CallTypeManager } from '@/managers/CallTypeManager';
import { PriorityManager } from '@/managers/PriorityManager';
import { checkIsLateReasonRequiredForRange } from '@/utils/lateReasonHelper';
import { ITrip } from '~/api/ITrip';

export interface ITripActionData {
  trip: ITrip & Record<string, unknown>;
  fromStatusId?: string;
  toStatusId?: string;
  toStatusLabel?: string;
  specialTimestampId?: string;
  dispatchAction: string;
  unitId?: string;
  reason?: unknown;
  reasonId?: string | number;
  profileTypeId?: string;
  responseProfileTimestamp?: IResponseProfileTimestamp;
  /** Shift ID for pre-assignment tracking (ZD-20165) */
  shiftId?: string;
  /** FE-only flag: indicates the transition is from Pre-Assigned state (for toast display) */
  fromPreAssigned?: boolean;
  /** Resource display name for toast messages (used when unitManager cannot resolve the name) */
  resourceName?: string;
  /** FE-only flag: the target resource is an inactive shift row (drives the toast wording) */
  isInactiveShift?: boolean;
}

type DispatchCallback = (action: string, payload: unknown) => Promise<unknown>;

export async function tryUnitAction(
  data: ITripActionData,
  dispatch?: DispatchCallback,
): Promise<unknown> {
  // ZD-20171: Pre-assign should bypass late reason checks entirely.
  // No notifications, no prompts — just assign with pre-assigned status.
  if (data.dispatchAction === TRIPS.DISPATCH_ACTIONS.PRE_ASSIGN_UNIT_TO_TRIP) {
    // A shift may exist without a vehicle, and pre-assignment must still be allowed on
    // it — the trip is linked by shiftId and the vehicle is resolved when the shift is
    // activated. So require either a unitId (vehicleId) or a shiftId; bail only when
    // there is nothing at all to link the trip to.
    //
    // Throw rather than returning quietly: callers report success right after awaiting
    // this, so a silent no-op showed a "trip pre-assigned" toast for a request that was
    // never sent.
    if (!data.unitId && !data.shiftId) {
      throw new Error('Pre-assignment requires either a unitId or a shiftId.');
    }

    await assignUnitToTrip({
      trip: data.trip as { id: string },
      unitId: data.unitId ?? '',
      reason: data.reason as AssignUnitToTripParams['reason'],
      tripStatus: TRIPS.STATUS.PRE_ASSIGNED,
      shiftId: data.shiftId,
      resourceName: data.resourceName,
      isInactiveShift: data.isInactiveShift,
    });

    return null;
  }

  const priorityManager = container.get(PriorityManager);
  const callTypeManager = container.get(CallTypeManager);

  const profile = priorityManager.find(data.trip.priority.id);
  const timestamps = (profile?.responseProfileInfo?.responseTimeStamp as unknown as IResponseProfileTimestamp[]) ?? [];
  const callType = callTypeManager.find(data.trip.callType.id);
  const allowedTimeStamps = callType?.allowedTimeStamps ?? [];

  let timestamp = null;
  let allowedTimestamp = null;
  let reasonPrompt = false;
  let lateReasonInfo = null;

  // Force-assign (Pre-Assigned '8' → Assigned '1') would fail the numeric guard below
  // (1 > 8 is false) and skip late-reason checks entirely. Treat force-assign as if the
  // trip were leaving OPEN so the walk over the Assigned status still happens.
  const effectiveFromStatusId = data.fromPreAssigned ? '0' : data.fromStatusId;

  if (Number(data.toStatusId) > (Number(effectiveFromStatusId) || 0)) {
    if (timestamps?.length > 0) {
      for (let step = Number(effectiveFromStatusId) + 1; step <= Number(data.toStatusId); step += 1) {
        timestamp = timestamps.find((item) => item.status === Number(step) && item.lateCapture === 1);
        allowedTimestamp = allowedTimeStamps.find((status: { id: string }) => Number(status.id) === Number(step));

        if (allowedTimestamp != null) {
          const isTimestampLateRequired = checkForRequiredLateReasons({
            trip: data.trip,
            responseProfileTimestamp: timestamp,
          });

          if (isTimestampLateRequired) {
            lateReasonInfo = { ...data, responseProfileTimestamp: timestamp };
            reasonPrompt = true;
            break;
          }
        }
      }
    }

    if (!reasonPrompt) {
      const isLateRequired = checkIsLateReasonRequiredForRange(
        data.trip,
        effectiveFromStatusId || '0',
        data.toStatusId || '0',
      );

      if (isLateRequired) {
        lateReasonInfo = { ...data, responseProfileTimestamp: timestamp };
        reasonPrompt = true;
      }
    }
  }

  if (!reasonPrompt) {
    if (data.dispatchAction === 'assignUnitToTrip') {
      if (!data.unitId) {
        return lateReasonInfo;
      }

      await assignUnitToTrip({
        trip: data.trip as { id: string },
        unitId: data.unitId,
        reason: data.reason as AssignUnitToTripParams['reason'],
      });
    }
    else if (data.dispatchAction === 'changeUnitStatus') {
      await changeUnitStatus({
        trip: data.trip as { id: string },
        toStatusId: data.toStatusId,
        fromStatusId: data.fromStatusId,
        toStatusLabel: data.toStatusLabel,
        specialTimestampId: data.specialTimestampId,
        reason: data.reason as ChangeUnitStatusParams['reason'],
        fromPreAssigned: data.fromPreAssigned,
      });
    }
    else if (dispatch) {
      await dispatch(data.dispatchAction, data);
    }
  }

  return lateReasonInfo;
}
